import React, { useState, useContext, useMemo, useEffect, useRef } from "react";
import MainStructure from "../components/containers/MainStructure";
import Step1 from "../components/NewPedigree/Step1";
import Step2 from "../components/NewPedigree/Step2";
import ReviewStep from "../components/NewPedigree/ReviewStep";
import ModelsModal from "../components/NewPedigree/ModelsModal";
import AuthModal from "../components/NewPedigree/AuthModal";
import { UserContext } from "../contexts/UserContext";
import useSourAlert from "../hooks/useSourAlert";
import SourAlert from "../components/elements/SourAlert";
import { authenticationService } from "../api/authentication.service";
import { userService } from "../api/user.service";
import { useLocation, useNavigate } from "react-router-dom";
import { membershipReports } from "../api";
import { horseService } from "../api/horses.service";

const STEPS = ["Details", "Parents", "Review"];

function hasSelection(value) {
	return Array.isArray(value) ? Boolean(value[0]) : Boolean(value);
}

export function isParentSelectionComplete(selected, parent, firstGrandparent, secondGrandparent) {
	if (hasSelection(selected[parent])) {
		return !hasSelection(selected[firstGrandparent]) && !hasSelection(selected[secondGrandparent]);
	}
	return hasSelection(selected[firstGrandparent]) && hasSelection(selected[secondGrandparent]);
}

export function isValidPedigreeSelection(selected) {
	if (!selected.ml_model || !selected.name || selected.name.trim() === "") return false;
	return (
		isParentSelectionComplete(selected, "sire", "sire_sire", "sire_dam") &&
		isParentSelectionComplete(selected, "dam", "dam_sire", "dam_dam")
	);
}

export function getParentsCompletionLabel(selected) {
	if (!hasSelection(selected.sire)) {
		if (hasSelection(selected.sire_sire) && !hasSelection(selected.sire_dam)) {
			return "Select a paternal granddam";
		}
		if (!hasSelection(selected.sire_sire) && hasSelection(selected.sire_dam)) {
			return "Select a paternal grandsire";
		}
		if (!(hasSelection(selected.sire_sire) && hasSelection(selected.sire_dam))) {
			return "Select a sire";
		}
	}
	if (!hasSelection(selected.dam)) {
		if (hasSelection(selected.dam_sire) && !hasSelection(selected.dam_dam)) {
			return "Select a maternal granddam";
		}
		if (!hasSelection(selected.dam_sire) && hasSelection(selected.dam_dam)) {
			return "Select a maternal grandsire";
		}
		if (!(hasSelection(selected.dam_sire) && hasSelection(selected.dam_dam))) {
			return "Select a dam";
		}
	}
	return "Review report";
}

function NewPedigree() {
	const navigate = useNavigate();
	const location = useLocation();
	const [isOpenModels, setIsOpenModels] = useState(false);
	const [pedigreeData, setPedigreeData] = useState({ ml_model: "1" });
	const user = useContext(UserContext);
	const [credits, setCredits] = useState(0);
	const [originalCredits, setOriginalCredits] = useState(0);
	const [idProduct, setIdProduct] = useState(0);
	const [alertProps, showAlert] = useSourAlert();
	const [horse, setHorse] = useState(null);
	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
	const [pendingReportBody, setPendingReportBody] = useState(null);
	const [isSubmittingReport, setIsSubmittingReport] = useState(false);
	const [sponsorIdToOpen, setSponsorIdToOpen] = useState(null);
	const [currentStep, setCurrentStep] = useState(1);
	const [selectionDetails, setSelectionDetails] = useState({});
	const attemptedSponsorTokensRef = useRef(new Set());
	const inFlightSponsorTokenRef = useRef(null);
	const previousParentCompletionRef = useRef({ sire: false, dam: false });

	const handleStep1Change = (newData) => {
		setPedigreeData((prev) => ({ ...prev, ...newData }));
	};

	function handleSelected(horse_id, type) {
		setPedigreeData((prev) => {
			const newData = { ...prev };
			if (horse_id) {
				newData[type] = [horse_id];
				if (type === "sire") {
					delete newData.sire_sire;
					delete newData.sire_dam;
				}
				if (type === "dam") {
					delete newData.dam_sire;
					delete newData.dam_dam;
				}
				if (type === "dam_sire" || type === "dam_dam") {
					delete newData.dam;
				}
				if (type === "sire_sire" || type === "sire_dam") {
					delete newData.sire;
				}
			} else {
				delete newData[type];
			}
			return newData;
		});
	}

	const canGenerateReport = useMemo(
		() => isValidPedigreeSelection(pedigreeData),
		[pedigreeData]
	);
	const sireSelectionComplete = isParentSelectionComplete(
		pedigreeData,
		"sire",
		"sire_sire",
		"sire_dam"
	);
	const damSelectionComplete = isParentSelectionComplete(
		pedigreeData,
		"dam",
		"dam_sire",
		"dam_dam"
	);
	const detailsComplete = Boolean(
		pedigreeData.ml_model && pedigreeData.name?.trim()
	);
	const parentsCompletionLabel = getParentsCompletionLabel(pedigreeData);
	const availableCredits =
		authenticationService.currentUserValue?.user?.credits ?? user?.credits ?? 0;
	const hasSession = Boolean(authenticationService.currentUserValue);

	useEffect(() => {
		const previous = previousParentCompletionRef.current;
		previousParentCompletionRef.current = {
			sire: sireSelectionComplete,
			dam: damSelectionComplete,
		};

		if (sireSelectionComplete && !previous.sire) {
			window.dispatchEvent(
				new CustomEvent("onboarding:pedigree-sire-complete")
			);
		}
		if (damSelectionComplete && !previous.dam) {
			window.dispatchEvent(
				new CustomEvent("onboarding:pedigree-dam-complete")
			);
		}
	}, [damSelectionComplete, sireSelectionComplete]);

	function handleSelectionDetailsChange(type, details) {
		setSelectionDetails((prev) => {
			if (!details) {
				const next = { ...prev };
				delete next[type];
				return next;
			}
			return { ...prev, [type]: details };
		});
	}

	function goToStep(step) {
		if (step === 1 || (step === 2 && detailsComplete) || (step === 3 && canGenerateReport)) {
			setCurrentStep(step);
			window.scrollTo?.({ top: 0, behavior: "smooth" });
		}
	}

	const selectedSireId = pedigreeData?.sire?.[0] || null;
	const isSponsoredSelection = Boolean(
		selectedSireId && horse?.id === selectedSireId && horse?.sponsor
	);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const token = params.get("token");
		if (!token) return;
		if (attemptedSponsorTokensRef.current.has(token)) {
			console.info("[sponsor] token already applied", token);
			return;
		}
		if (inFlightSponsorTokenRef.current === token) {
			console.info("[sponsor] token already in flight", token);
			return;
		}

		inFlightSponsorTokenRef.current = token;
		console.info("[sponsor] resolving token", token);

		horseService
			.resolveSponsoredToken(token)
			.then((response) => {
				console.info("[sponsor] resolve response", response);
				attemptedSponsorTokensRef.current.add(token);
				inFlightSponsorTokenRef.current = null;
				setHorse(response.sire);
				setSponsorIdToOpen(response.sire.sponsor?.id ?? null);
				setPedigreeData((prev) => ({
					...prev,
					sire: [response.sire.id],
					ml_model: String(response.ml_model),
					name: response.default_name,
				}));
				setSelectionDetails((prev) => ({ ...prev, sire: response.sire }));
				console.info("[sponsor] applied defaults", {
					sire: [response.sire.id],
					ml_model: String(response.ml_model),
					name: response.default_name,
				});
			})
			.catch((error) => {
				inFlightSponsorTokenRef.current = null;
				console.error("[sponsor] resolve failed", error);
				showAlert({
					type: "error",
					title: "Invalid sponsor link",
					description:
						"We could not validate this sponsor link. You can still create a pedigree normally.",
						okButtonText: "Ok",
					});
				});
		}, [location.search, showAlert]);

	useEffect(() => {
		console.info("[sponsor] pedigreeData updated", {
			ml_model: pedigreeData.ml_model,
			sire: pedigreeData.sire,
			name: pedigreeData.name,
		});
	}, [pedigreeData.ml_model, pedigreeData.name, pedigreeData.sire]);

	function buildReportBody() {
		return {
			metadata: pedigreeData,
			product: idProduct,
			product_type: "basic",
		};
	}

	function submitReport(body) {
		if (isSubmittingReport) return;
		setIsSubmittingReport(true);
		generateReport(body);
	}

	function handleGenerateIntent() {
		if (!canGenerateReport) {
			showAlert({
				type: "error",
				title: "Incomplete Data",
				description:
					"Please ensure you have selected a name, a model and either a Sire (or both paternal grandparents) and either a Dam (or both maternal grandparents).",
				okButtonText: "Ok",
			});
			return;
		}
		const body = buildReportBody();
		const hasSession = !!authenticationService.currentUserValue;

		if (!hasSession) {
			setPendingReportBody(body);
			setIsAuthModalOpen(true);
			return;
		}

		if (isSponsoredSelection) {
			let sponsorName = "";
			if (horse.sponsor) {
				sponsorName = horse.sponsor.name;
			}
			showAlert({
				type: "warning",
				title: `Sponsored Report`,
				description: `This report costs no credits because you selected a sponsored stallion by ${sponsorName}.`,
				showCancelButton: true,
				cancelButtonText: "Cancel",
				showOkButton: true,
				okButtonText: "Generate",
					onOk: () => submitReport(body),
				});
				return;
			}
		if (availableCredits < credits) {
			showAlert({
				type: "error",
				title: "Insufficient credits",
				description:
					"You do not have enough credits in your account to generate your report.",
				showCancelButton: true,
				cancelButtonText: "Buy credits",
				onCancel: () => navigate("/membership"),
			});
			return;
		}
		showAlert({
			type: "warning",
			title: `This report costs ${credits} credits`,
			description:
				"Once you accept, the credits for the report will be deducted from your account. Do you wish to continue?",
			showCancelButton: true,
			cancelButtonText: "Cancel",
			showOkButton: true,
			okButtonText: "Yes",
			onOk: () => submitReport(body),
		});
	}

	function generateReport(body) {
		membershipReports
			.create_report(body)
			.then(() => {
				setIsSubmittingReport(false);
				window.dispatchEvent(
					new CustomEvent("onboarding:pedigree-report-created")
				);
				window.dispatchEvent(new Event("marketing:events-ready"));
				showAlert({
					type: "success",
					title: "Report generation initiated",
					description:
						"Your report is being generated. Please wait a few moments and check Pedigree Analysis once it's ready.",
					okButtonText: "Accept",
					onOk: () => {
						userService
							.details()
							.then((updatedUser) => {
								authenticationService.updateUserDetails(
									updatedUser
								);
								navigate("/pedigree-analysis");
							})
							.catch((error) =>
								console.error(
									"Error updating user details:",
									error
								)
							);
					},
					});
			})
			.catch((error) => {
				setIsSubmittingReport(false);
				console.error("Error generating report:", error);

				const errorDetail =
					error?.detail ||
					error?.non_field_errors?.[0] ||
					error?.metadata?.[0];
				if (
					typeof errorDetail === "string" &&
					errorDetail.toLowerCase().includes("enough credits")
				) {
					showAlert({
						type: "error",
						title: "Insufficient credits",
						description:
							"You do not have enough credits in your account to generate your report.",
						showCancelButton: true,
						cancelButtonText: "Buy credits",
						onCancel: () => navigate("/membership"),
					});
					return;
				}

				showAlert({
					type: "error",
					title: "Error",
					description:
						"An error occurred while generating the report. Please try again.",
					okButtonText: "Ok",
				});
			});
	}

	function handleAuthenticated() {
		setIsAuthModalOpen(false);
		if (!pendingReportBody) return;
		const body = pendingReportBody;
		submitReport(body);
		setPendingReportBody(null);
	}

	return (
		<>
			<MainStructure showSidebar={!!authenticationService.currentUserValue}>
				<div className="new-pedigree-page">
				<div className="new-pedigree-header">
					<p className="new-pedigree-eyebrow">Pedigree analysis</p>
					<h1>Create a pedigree report</h1>
					<p>Build a report in three quick steps. Your selections are kept as you move between steps.</p>
				</div>
				<nav className="pedigree-progress" aria-label="Pedigree report progress">
					<ol>
						{STEPS.map((label, index) => {
							const step = index + 1;
							const isAvailable = step === 1 || (step === 2 && detailsComplete) || (step === 3 && canGenerateReport);
							return (
								<li key={label} className={step < currentStep ? "is-complete" : step === currentStep ? "is-current" : ""}>
									<button
										type="button"
										onClick={() => goToStep(step)}
										disabled={!isAvailable}
										aria-current={step === currentStep ? "step" : undefined}
									>
										<span className="pedigree-progress__number" aria-hidden="true">
											{step < currentStep ? <i className="fas fa-check" /> : step}
										</span>
										<span>{label}</span>
									</button>
								</li>
							);
						})}
					</ol>
				</nav>
				<div className="pedigree-step-region" aria-live="polite">
				{currentStep === 1 && (
				<div className="step1-container">
					<Step1
						selected={pedigreeData}
						handleStep1Change={handleStep1Change}
						setIsOpenModels={setIsOpenModels}
						setCredits={setCredits}
						setOriginalCredits={setOriginalCredits}
						setIdProduct={setIdProduct}
						credits={credits}
						originalCredits={originalCredits}
						horse={horse}
						canContinue={detailsComplete}
						onContinue={() => goToStep(2)}
					/>
				</div>
				)}
				{currentStep === 2 && (
				<div className="step2-container">
					<Step2
						handleSelected={handleSelected}
						setCredits={setCredits}
						originalCredits={originalCredits}
						canGenerateReport={canGenerateReport}
						selected={pedigreeData}
						horse={horse}
						setHorse={setHorse}
						sponsorIdToOpen={sponsorIdToOpen}
						onBack={() => goToStep(1)}
						onContinue={() => goToStep(3)}
						completionLabel={parentsCompletionLabel}
						selectionDetails={selectionDetails}
						onSelectionDetailsChange={handleSelectionDetailsChange}
					/>
				</div>
				)}
				{currentStep === 3 && (
					<ReviewStep
						selected={pedigreeData}
						selectionDetails={selectionDetails}
						cost={credits}
						availableCredits={availableCredits}
						hasSession={hasSession}
						isSponsored={isSponsoredSelection}
						isSubmitting={isSubmittingReport}
						onBack={() => goToStep(2)}
						onEditDetails={() => goToStep(1)}
						onGenerate={handleGenerateIntent}
					/>
				)}
				</div>
				</div>
				<SourAlert {...alertProps} />
			</MainStructure>
			<ModelsModal
				isOpenModels={isOpenModels}
				setIsOpenModels={setIsOpenModels}
			/>
			<AuthModal
				isOpen={isAuthModalOpen}
				onClose={() => setIsAuthModalOpen(false)}
				onAuthenticated={handleAuthenticated}
			/>
		</>
	);
}

export default NewPedigree;
